<template>
  <Teleport to="body">
    <!-- Background -->
    <Transition
      name="modal-fade"
      appear>
      <div
        v-if="stack.length > 0"
        class="js-host-modal fixed top-[var(--env-bar-height)] left-0 z-1000 h-[calc(100dvh-var(--env-bar-height))] w-full bg-black/35" />
    </Transition>

    <!-- Dialog -->
    <Transition
      name="modal-fade"
      appear>
      <div
        v-if="stack.length > 0"
        class="js-host-modal fixed top-[var(--env-bar-height)] left-0 z-1001 flex h-[calc(100dvh-var(--env-bar-height))] w-full items-start overflow-auto">
        <div
          v-for="(entry, index) in stack"
          :key="entry.id"
          v-show="index === stack.length - 1"
          ref="stackItemEl"
          role="dialog"
          aria-modal="true"
          :class="[
            'relative mx-auto my-[10vh] min-h-50 max-w-[90%] rounded-xl bg-surface shadow-sm',
            entry.width === 'sm' ? 'w-115' : '',
            entry.width === 'md' ? 'w-175' : '',
            entry.width === 'lg' ? 'w-200' : '',
            entry.width === 'xl' ? 'w-250' : '',
            entry.width === 'xxl' ? 'w-350' : '',
            // Four-column or master-detail content on a desktop screen: most of the width, capped.
            entry.width === 'xxxl' ? 'w-[min(90vw,1700px)]' : '',
            !entry.width ? 'w-175' : '',
          ]">
          <component
            v-if="registry[entry.name]"
            :is="registry[entry.name]"
            v-bind="entry.props"
            @close="closeTop" />
          <div
            v-if="!entry.hideCloseButton"
            class="absolute top-0 right-0 h-12 w-12 cursor-pointer p-3 text-text-primary"
            @click="requestCloseTop">
            <IconLucideX />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts">
import { mapState, mapActions } from 'pinia'
import { useModalStore } from '@/stores/modalStore'
import { focusFirstElement, trapTabFocus, wireDialogLabel } from '@/globals/typescript/utils/focusManagement'
import { useDismissablePriority } from '@team-milva/lab-components'
import {
  confirmLeaveUnsaved,
  isModalDirty,
} from '@/composables/useUnsavedChangesGuard'

/**
 * Import all modal components that can be shown globally
 * (You can lazy-load these if you want)
 */
import ConfirmationModal from '@/components/modals/ConfirmationModal.vue'
import ConfirmScheduledSendModal from '@/components/modals/ConfirmScheduledSendModal.vue'
import UnsavedChangesModal from '@/components/modals/UnsavedChangesModal.vue'
import InviteModal from '@/components/modals/InviteModal.vue'
import CreateEmployeeModal from '@/components/modals/CreateEmployeeModal.vue'
import ChangeAccountModal from '@/components/modals/ChangeAccountModal.vue'
import ImpersonationModal from '@/components/modals/ImpersonationModal.vue'
import CreateLabEquipmentModal from '@/components/modals/CreateLabEquipmentModal.vue'
import CreateLabLocationModal from '@/components/modals/CreateLabLocationModal.vue'
import ChangeSampleLocationModal from '@/components/modals/ChangeSampleLocationModal.vue'
import AddLabEquipmentMaintenanceModal from '@/components/modals/AddLabEquipmentMaintenanceModal.vue'
import CreateAnalysisTestModal from '@/components/modals/CreateAnalysisTestModal.vue'
import CreateAnalysisModal from '@/components/modals/CreateAnalysisModal.vue'
import CreateSampleAreaModal from '@/components/modals/CreateSampleAreaModal.vue'
import QuickCreateSampleAreaModal from '@/components/modals/QuickCreateSampleAreaModal.vue'
import CreateClosedDayModal from '@/components/modals/CreateClosedDayModal.vue'
import CreateTatModal from '@/components/modals/CreateTatModal.vue'
import CreateAddonServiceModal from '@/components/modals/CreateAddonServiceModal.vue'
import CreateTextListItemModal from '@/components/modals/CreateTextListItemModal.vue'
import ClonePricingSegmentModal from '@/components/modals/ClonePricingSegmentModal.vue'
import CreatePricingSegmentModal from '@/components/modals/CreatePricingSegmentModal.vue'
import PricingSegmentAnalysisPickerModal from '@/components/modals/PricingSegmentAnalysisPickerModal.vue'
import PricingSegmentDealModal from '@/components/modals/PricingSegmentDealModal.vue'
import CustomerAnalysisPickerModal from '@/components/modals/CustomerAnalysisPickerModal.vue'
import TatAnalysisPickerModal from '@/components/modals/TatAnalysisPickerModal.vue'
import DiscardCustomPricesModal from '@/components/modals/DiscardCustomPricesModal.vue'
import CreateCustomerModal from '@/components/modals/CreateCustomerModal.vue'
import CreateOrderModal from '@/components/modals/CreateOrderModal.vue'
import CancelOrderModal from '@/components/modals/CancelOrderModal.vue'
import CreditInvoiceModal from '@/components/modals/CreditInvoiceModal.vue'
import OverrideLinePriceModal from '@/components/modals/OverrideLinePriceModal.vue'
import ResolveUnknownLinePriceModal from '@/components/modals/ResolveUnknownLinePriceModal.vue'
import CreateDropPointModal from '@/components/modals/CreateDropPointModal.vue'
import CreateReportTemplateModal from '@/components/modals/CreateReportTemplateModal.vue'
import CreateDisplayModal from '@/components/modals/CreateDisplayModal.vue'
import ReportTemplateVariablesModal from '@/components/modals/ReportTemplateVariablesModal.vue'
import CreateReportDescriptionModal from '@/components/modals/CreateReportDescriptionModal.vue'
import CreateReportDescriptionItemModal from '@/components/modals/CreateReportDescriptionItemModal.vue'
import CreatePrivateDropPointModal from '@/components/modals/CreatePrivateDropPointModal.vue'
import InviteCustomerUserBusinessModal from '@/components/modals/InviteCustomerUserBusinessModal.vue'
import CreateCustomerUserBusinessModal from '@/components/modals/CreateCustomerUserBusinessModal.vue'
import ImportCustomerUsersBusinessModal from '@/components/modals/ImportCustomerUsersBusinessModal.vue'
import SelectContactPersonModal from '@/components/modals/SelectContactPersonModal.vue'
import EnterAnalysisResultsModal from '@/components/modals/EnterAnalysisResultsModal.vue'
import UserProfileModal from '@/components/modals/UserProfileModal/UserProfileModal.vue'
import EnterPinModal from '@/components/modals/EnterPinModal.vue'
import UpdatePinCodeModal from '@/components/modals/UpdatePinCodeModal.vue'
import OrderReportPreviewModal from '@/components/modals/OrderReportPreviewModal.vue'
import InvoicePdfPreviewModal from '@/components/modals/InvoicePdfPreviewModal.vue'
import OrderCustomerReportPreviewModal from '@/components/modals/OrderCustomerReportPreviewModal.vue'
import OrderReportDescriptionsModal from '@/components/modals/OrderReportDescriptionsModal.vue'
import OrderReportSampleDescriptionsModal from '@/components/modals/OrderReportSampleDescriptionsModal.vue'
import OrderReportOrderDescriptionModal from '@/components/modals/OrderReportOrderDescriptionModal.vue'
import OrderReportRecipientsModal from '@/components/modals/OrderReportRecipientsModal.vue'
import ChangeOrderCustomerModal from '@/components/modals/ChangeOrderCustomerModal.vue'
import CreateBilagLibraryItemModal from '@/components/modals/CreateBilagLibraryItemModal.vue'
import BilagPreviewModal from '@/components/modals/BilagPreviewModal.vue'
import OrderReportBilagModal from '@/components/modals/OrderReportBilagModal.vue'
import OrderResendReportsModal from '@/components/modals/OrderResendReportsModal.vue'
import OrderReportResponsibleUserModal from '@/components/modals/OrderReportResponsibleUserModal.vue'
import UpdateOrderTatsModal from '@/components/modals/UpdateOrderTatsModal.vue'
import OrderAddonServicesModal from '@/components/modals/OrderAddonServicesModal.vue'
import RegisterSampleTatStartDateModal from '@/components/modals/RegisterSampleTatStartDateModal.vue'
import UpdateOrderTatStartDateModal from '@/components/modals/UpdateOrderTatStartDateModal.vue'
import DhlCredentialsModal from '@/components/modals/DhlCredentialsModal.vue'
import DhlSandboxConnectModal from '@/components/modals/DhlSandboxConnectModal.vue'
import DhlShipmentsModal from '@/components/modals/DhlShipmentsModal.vue'
import UpdateOrderReportLocaleModal from '@/components/modals/UpdateOrderReportLocaleModal.vue'
import EditSampleModal from '@/components/modals/EditSampleModal.vue'
import ResendBcInvoiceModal from '@/components/modals/ResendBcInvoiceModal.vue'
import CreateTestBcItemsModal from '@/components/modals/CreateTestBcItemsModal.vue'
import CreateTestBcCustomersModal from '@/components/modals/CreateTestBcCustomersModal.vue'
import KeyboardShortcutsModal from '@/components/modals/KeyboardShortcutsModal.vue'
import ReceiveOrdersModal from '@/components/modals/ReceiveOrdersModal.vue'
import ReceiveOrderSamplesModal from '@/components/modals/ReceiveOrderSamplesModal.vue'
import ForwardSamplesModal from '@/components/modals/ForwardSamplesModal.vue'
import DashboardCustomizeModal from '@/components/modals/DashboardCustomizeModal.vue'
import SeedOrdersModal from '@/components/modals/SeedOrdersModal.vue'

export default {
  name: 'ModalHost',

  data() {
    return {
      previousActiveElement: null as HTMLElement | null,
      dismissable: useDismissablePriority(),
    }
  },

  computed: {
    ...mapState(useModalStore, ['top', 'stack']),
    /**
     * Modal registry:
     * key = modal name used by modal.open(name)
     * value = Vue component
     */
    registry(): Record<string, unknown> {
      return {
        ConfirmationModal,
        ConfirmScheduledSendModal,
        UnsavedChangesModal,
        InviteModal,
        CreateEmployeeModal,
        ChangeAccountModal,
        ImpersonationModal,
        CreateLabEquipmentModal,
        CreateLabLocationModal,
        ChangeSampleLocationModal,
        AddLabEquipmentMaintenanceModal,
        CreateAnalysisTestModal,
        CreateAnalysisModal,
        CreateSampleAreaModal,
        QuickCreateSampleAreaModal,
        CreateClosedDayModal,
        CreateTatModal,
        CreateAddonServiceModal,
        CreateTextListItemModal,
        ClonePricingSegmentModal,
        CreatePricingSegmentModal,
        PricingSegmentAnalysisPickerModal,
        PricingSegmentDealModal,
        CustomerAnalysisPickerModal,
        TatAnalysisPickerModal,
        DiscardCustomPricesModal,
        CreateCustomerModal,
        CreateOrderModal,
        CancelOrderModal,
        CreditInvoiceModal,
        OverrideLinePriceModal,
        ResolveUnknownLinePriceModal,
        CreateDropPointModal,
        CreateReportTemplateModal,
        CreateDisplayModal,
        ReportTemplateVariablesModal,
        CreateReportDescriptionModal,
        CreateReportDescriptionItemModal,
        CreatePrivateDropPointModal,
        InviteCustomerUserBusinessModal,
        CreateCustomerUserBusinessModal,
        ImportCustomerUsersBusinessModal,
        SelectContactPersonModal,
        EnterAnalysisResultsModal,
        UserProfileModal,
        EnterPinModal,
        UpdatePinCodeModal,
        OrderReportDescriptionsModal,
        OrderReportSampleDescriptionsModal,
        OrderReportOrderDescriptionModal,
        OrderReportRecipientsModal,
        ChangeOrderCustomerModal,
        CreateBilagLibraryItemModal,
        BilagPreviewModal,
        OrderReportBilagModal,
        OrderResendReportsModal,
        OrderReportPreviewModal,
        InvoicePdfPreviewModal,
        OrderCustomerReportPreviewModal,
        OrderReportResponsibleUserModal,
        UpdateOrderTatsModal,
        OrderAddonServicesModal,
        RegisterSampleTatStartDateModal,
        UpdateOrderTatStartDateModal,
        DhlCredentialsModal,
        DhlSandboxConnectModal,
        DhlShipmentsModal,
        UpdateOrderReportLocaleModal,
        EditSampleModal,
        ResendBcInvoiceModal,
        CreateTestBcItemsModal,
        CreateTestBcCustomersModal,
        KeyboardShortcutsModal,
        ReceiveOrdersModal,
        ReceiveOrderSamplesModal,
        ForwardSamplesModal,
        DashboardCustomizeModal,
        SeedOrdersModal,
      }
    },
  },
  watch: {
    // Only remembers what to give focus back to once the last modal closes. Which modal gets focus
    // while some are open is the `top.id` watcher's job: this one cannot see a modal being replaced
    // by another in the same tick (a confirmation closing as it opens the next one) — the count
    // goes 1 → 0 → 1 and Vue reports no change at all.
    'stack.length'(newLength: number, oldLength: number) {
      if (newLength > oldLength && oldLength === 0) {
        this.previousActiveElement = document.activeElement as HTMLElement | null
      } else if (newLength < oldLength && newLength === 0) {
        this.previousActiveElement?.focus?.()
        this.previousActiveElement = null
      }
    },
    // A different modal is now on top — opened over another, revealed by a close, or swapped in
    // for the one that just closed: it takes focus.
    'top.id'(newID: string | undefined) {
      if (newID) {
        this.$nextTick(this.onTopModalReady)
      }
    },
    top(newTop: unknown, oldTop: unknown) {
      if (newTop && !oldTop) {
        this.dismissable.activate()
      } else if (!newTop && oldTop) {
        this.dismissable.deactivate()
      }
    },
  },
  methods: {
    ...mapActions(useModalStore, ['closeTop']),
    // The two close paths ModalHost owns itself — Escape and the X button.
    // A modal's own `@close` is deliberately left unguarded: it fires after a
    // successful save too, where the form is still "dirty" relative to the
    // empty state it started from, and warning there would be nonsense.
    //
    // Only the top entry's own claim is consulted. Closing a modal does not
    // unmount whatever sits beneath it, so a dirty editor underneath is not
    // at risk here — the router guard covers that case instead.
    async requestCloseTop() {
      const topEntry = this.top
      if (!topEntry || !isModalDirty(topEntry.id)) {
        this.closeTop()
        return
      }

      const shouldLeave = await confirmLeaveUnsaved()
      // The confirmation itself was pushed onto this same stack and has
      // already popped itself, so one more pop closes the guarded modal.
      if (shouldLeave) {
        this.closeTop()
      }
    },
    getTopModalEl(): HTMLElement | null {
      const stackItemEls = this.$refs.stackItemEl as HTMLElement[] | undefined
      if (!stackItemEls?.length) {
        return null
      }
      return stackItemEls[stackItemEls.length - 1] ?? null
    },
    onTopModalReady() {
      focusFirstElement(this.getTopModalEl())
      wireDialogLabel(this.getTopModalEl())
    },
    onKeydown(e: KeyboardEvent) {
      if (!this.top) {
        return
      }

      if (e.key === 'Escape') {
        // Capture-phase listener, so this normally fires before anything
        // nested inside the modal even sees the key. dismissable.isTop()
        // defers to whatever was opened most recently (a combobox dropdown,
        // date picker, etc.) so that layer gets to close itself first — the
        // modal only reacts once nothing on top of it is claiming the key.
        // This also preserves the original "modal beats UiFullScreenModal"
        // ordering, since the modal always activates after any full-screen
        // modal underneath it.
        if (!this.dismissable.isTop()) {
          return
        }

        e.preventDefault()
        e.stopPropagation()
        if (this.top?.useEasyClose) {
          void this.requestCloseTop()
        }
        return
      }

      if (e.key === 'Tab') {
        // Same deferral as Escape above: if something opened on top of this
        // modal (e.g. a GlobalMenuHost dropdown menu triggered from inside
        // it) currently holds focus, don't fight it — trapTabFocus would
        // otherwise see focus as having "escaped" this dialog's container
        // and force it back here on every Tab press.
        if (!this.dismissable.isTop()) {
          return
        }
        trapTabFocus(e, this.getTopModalEl())
      }
    },
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown, { passive: false, capture: true })
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown as EventListener, { capture: true })
    this.dismissable.deactivate()
  },
}
</script>

<style scoped>
/* Fade */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 120ms ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>

